'use client';

import Image from 'next/image';
import Link from 'next/link';
import { useEffect, useMemo, useState } from 'react';
import {
  bodyTypeOptions,
  breastSizeOptions,
  buttSizeOptions,
  detailCards,
  ethnicityOptions,
  eyeColorOptions,
  firstNames,
  GenderKey,
  genderOptions,
  hairColorOptions,
  hairStyleOptions,
  initialQuizState,
  lastNames,
  QuizOption,
  QuizState,
  StyleKey,
  styleOptions
} from '@/lib/quiz-config';
import { persistAttribution, readAttribution, trackLandingEvent } from '@/lib/attribution';

const steps = [
  { label: 'Style', icon: '✦' },
  { label: 'Look', icon: '◈' },
  { label: 'Face', icon: '✧' },
  { label: 'Body', icon: '◆' },
  { label: 'Details', icon: '♡' },
  { label: 'Ready', icon: '✓' }
];

function cx(...classes: Array<string | false | null | undefined>) {
  return classes.filter(Boolean).join(' ');
}

const getStyleKey = (style: string): StyleKey => (style === 'anime' ? 'anime' : 'realistic');
const getGenderKey = (gender: string): GenderKey => (gender === 'trans' ? 'trans' : 'female');

const resolveOptionImage = (option: Pick<QuizOption, 'image' | 'imageByStyle' | 'imageByGenderStyle'>, style: string, gender = 'female') => {
  const styleKey = getStyleKey(style);
  return option.imageByGenderStyle?.[getGenderKey(gender)]?.[styleKey] ?? option.imageByStyle?.[styleKey] ?? option.image ?? '';
};

const resolveOptionVideo = (option: Pick<QuizOption, 'video' | 'videoByGender'>, gender = 'female') => {
  return option.videoByGender?.[getGenderKey(gender)] ?? option.video ?? '';
};

function optionById(options: QuizOption[], id: string) {
  return options.find((option) => option.id === id) ?? options[0];
}

function Stepper({ currentStep, setStep }: { currentStep: number; setStep: (step: number) => void }) {
  return (
    <div className="stepper" aria-label="Create progress">
      {steps.map((step, index) => {
        const complete = index < currentStep;
        const active = index === currentStep;
        return (
          <button
            key={step.label}
            className={cx('step-node', complete && 'complete', active && 'active')}
            disabled={index > currentStep}
            onClick={() => setStep(index)}
            aria-label={step.label}
          >
            <span>{complete ? '✓' : step.icon}</span>
          </button>
        );
      })}
    </div>
  );
}

function PillSwitch({
  options,
  value,
  onChange
}: {
  options: QuizOption[];
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <div className="pill-switch">
      {options.map((option) => (
        <button
          key={option.id}
          className={cx('pill-option', value === option.id && 'selected')}
          onClick={() => onChange(option.id)}
        >
          <span className="pill-dot" />
          {option.label}
        </button>
      ))}
    </div>
  );
}

function ImageCard({
  option,
  selected,
  onClick,
  large = false,
  style = 'realistic',
  gender = 'female'
}: {
  option: QuizOption;
  selected: boolean;
  onClick: () => void;
  large?: boolean;
  style?: string;
  gender?: string;
}) {
  const image = resolveOptionImage(option, style, gender);
  const video = resolveOptionVideo(option, gender);

  return (
    <button className={cx('image-card', large && 'large', selected && 'selected')} onClick={onClick}>
      {video ? (
        <video
          className="image-card-picture image-card-video"
          src={video}
          autoPlay
          loop
          muted
          playsInline
          aria-label={option.label}
        />
      ) : image ? (
        <Image
          className="image-card-picture"
          src={image}
          alt={option.label}
          width={large ? 802 : 341}
          height={large ? 1080 : 480}
          sizes={large ? '267px' : '128px'}
          priority={large}
        />
      ) : (
        <div className="empty-card-art" />
      )}
      <span className="check-badge">✓</span>
      <span className="card-label">{option.label}</span>
      {option.description ? <span className="card-description">{option.description}</span> : null}
    </button>
  );
}

function TextChoiceGrid({ options, value, onChange }: { options: string[]; value: string; onChange: (v: string) => void }) {
  return (
    <div className="text-choice-grid">
      {options.map((option) => (
        <button key={option} className={cx('text-choice', value === option && 'selected')} onClick={() => onChange(option)}>
          {option}
        </button>
      ))}
    </div>
  );
}

function EyeColorPicker({ value, style, onChange }: { value: string; style: string; onChange: (v: string) => void }) {
  const selected = eyeColorOptions.find((option) => option.id === value) ?? eyeColorOptions[0];
  const image = resolveOptionImage(selected, style);

  return (
    <div className="eye-color-picker">
      <div className={cx('eye-preview', getStyleKey(style) === 'anime' && 'anime-preview')}>
        <Image src={image} alt={selected.label} fill sizes="700px" priority />
      </div>
      <div className="eye-swatch-grid" role="radiogroup" aria-label="Eye Color">
        {eyeColorOptions.map((option) => (
          <button
            key={option.id}
            className={cx('eye-swatch-option', value === option.id && 'selected')}
            onClick={() => onChange(option.id)}
            role="radio"
            aria-checked={value === option.id}
          >
            <span className="eye-swatch" style={{ background: option.color }} />
            <span>{option.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return <h3 className="section-title">{children}</h3>;
}

function StyleStep({ state, update }: { state: QuizState; update: (patch: Partial<QuizState>) => void }) {
  return (
    <div className="quiz-panel centered-panel">
      <h1 className="quiz-title">Create Your <span>AI Lover</span></h1>
      <PillSwitch options={genderOptions} value={state.gender} onChange={(gender) => update({ gender })} />
      <div className="style-cards">
        {styleOptions.map((option) => (
          <ImageCard
            key={option.id}
            option={option}
            selected={state.style === option.id}
            onClick={() => update({ style: option.id })}
            large
            style={state.style}
            gender={state.gender}
          />
        ))}
      </div>
      <div className="flow-hint">
        <span>Pick her look</span>
        <span>Shape her personality</span>
        <span>Unlock private chat</span>
      </div>
    </div>
  );
}

function LookStep({ state, update }: { state: QuizState; update: (patch: Partial<QuizState>) => void }) {
  return (
    <div className="quiz-panel">
      <SectionTitle>Ethnicity</SectionTitle>
      <div className="card-grid ethnicity-grid">
        {ethnicityOptions.map((option) => (
          <ImageCard key={option.id} option={option} selected={state.ethnicity === option.id} onClick={() => update({ ethnicity: option.id })} style={state.style} gender={state.gender} />
        ))}
      </div>
      <SectionTitle>Age</SectionTitle>
      <div className="age-slider-wrap">
        <input type="range" min={18} max={50} value={state.age} onChange={(e) => update({ age: Number(e.target.value) })} />
        <div className="age-value" style={{ left: `${((state.age - 18) / 32) * 100}%` }}>{state.age}</div>
        <div className="age-marks">
          {[18, 22, 26, 30, 34, 38, 42, 46, 50].map((age) => <span key={age}>{age}</span>)}
        </div>
      </div>
    </div>
  );
}

function FaceStep({ state, update }: { state: QuizState; update: (patch: Partial<QuizState>) => void }) {
  return (
    <div className="quiz-panel">
      <SectionTitle>Eye Color</SectionTitle>
      <EyeColorPicker value={state.eyeColor} style={state.style} onChange={(eyeColor) => update({ eyeColor })} />
      <SectionTitle>Hair Color</SectionTitle>
      <div className="card-grid compact-grid">
        {hairColorOptions.map((option) => <ImageCard key={option.id} option={option} selected={state.hairColor === option.id} onClick={() => update({ hairColor: option.id })} style={state.style} gender={state.gender} />)}
      </div>
      <SectionTitle>Hair Style</SectionTitle>
      <div className="card-grid compact-grid">
        {hairStyleOptions.map((option) => <ImageCard key={option.id} option={option} selected={state.hairStyle === option.id} onClick={() => update({ hairStyle: option.id })} style={state.style} gender={state.gender} />)}
      </div>
    </div>
  );
}

function BodyStep({ state, update }: { state: QuizState; update: (patch: Partial<QuizState>) => void }) {
  return (
    <div className="quiz-panel">
      <SectionTitle>Body Type</SectionTitle>
      <div className="card-grid body-grid">
        {bodyTypeOptions.map((option) => <ImageCard key={option.id} option={option} selected={state.bodyType === option.id} onClick={() => update({ bodyType: option.id })} style={state.style} gender={state.gender} />)}
      </div>
      <SectionTitle>Breast Size</SectionTitle>
      <div className="card-grid body-grid">
        {breastSizeOptions.map((option) => <ImageCard key={option.id} option={option} selected={state.breastSize === option.id} onClick={() => update({ breastSize: option.id })} style={state.style} gender={state.gender} />)}
      </div>
      <SectionTitle>Butt Size</SectionTitle>
      <div className="card-grid body-grid">
        {buttSizeOptions.map((option) => <ImageCard key={option.id} option={option} selected={state.buttSize === option.id} onClick={() => update({ buttSize: option.id })} style={state.style} gender={state.gender} />)}
      </div>
    </div>
  );
}

function DetailsStep({ state, update, randomizeName }: { state: QuizState; update: (patch: Partial<QuizState>) => void; randomizeName: () => void }) {
  return (
    <div className="quiz-panel details-panel">
      <SectionTitle>Character Name</SectionTitle>
      <div className="name-row">
        <input value={state.name} onChange={(e) => update({ name: e.target.value })} placeholder="Enter character name..." />
        <button onClick={randomizeName} aria-label="Generate name">✦</button>
      </div>
      <SectionTitle>Personality Details</SectionTitle>
      <div className="detail-grid">
        {detailCards.map((card) => (
          <button className="detail-card" key={card.key}>
            <Image src={card.image} alt="" width={64} height={64} />
            <span>
              <strong>{card.label}</strong>
              <small>{card.value}</small>
            </span>
            <em>›</em>
          </button>
        ))}
      </div>
      <div className="textareas">
        <label>
          <span>Greeting <em>Optional</em></span>
          <textarea value={state.greeting} onChange={(e) => update({ greeting: e.target.value })} placeholder="Enter greeting ..." maxLength={1000} />
        </label>
        <label>
          <span>Scenario <em>Optional</em></span>
          <textarea value={state.scenario} onChange={(e) => update({ scenario: e.target.value })} placeholder="Shown at the start of the chat ..." maxLength={1000} />
        </label>
      </div>
    </div>
  );
}

function ReviewStep({ state, onStart }: { state: QuizState; onStart: () => void }) {
  const ethnicity = optionById(ethnicityOptions, state.ethnicity);
  const style = optionById(styleOptions, state.style);
  const previewImage = resolveOptionImage(ethnicity, state.style, state.gender);
  const summary = [
    ['Ethnicity', ethnicity.label],
    ['Age', String(state.age)],
    ['Hair Style', optionById(hairStyleOptions, state.hairStyle).label],
    ['Hair Color', optionById(hairColorOptions, state.hairColor).label],
    ['Eyes', eyeColorOptions.find((option) => option.id === state.eyeColor)?.label ?? state.eyeColor],
    ['Body Type', optionById(bodyTypeOptions, state.bodyType).label],
    ['Breast Size', optionById(breastSizeOptions, state.breastSize).label],
    ['Butt Size', optionById(buttSizeOptions, state.buttSize).label]
  ];

  return (
    <div className="review-layout">
      <div className="character-preview">
        <div className="preview-orb" />
        <Image src={previewImage} alt="Your Character" width={360} height={460} priority />
        <div className="preview-glow" />
      </div>
      <div className="review-card">
        <p className="review-label">Name Character</p>
        <h2>{state.name}</h2>
        <div className="ready-meter"><strong>100%</strong><span>Ready to create</span></div>
        <div className="main-style">
          <Image src={style.image ?? ''} alt={style.label} width={88} height={88} />
          <div>
            <p>Main Style</p>
            <strong>{style.label}</strong>
            <span>Private, visual, and ready for your first chat.</span>
          </div>
        </div>
        <div className="summary-grid">
          {summary.map(([label, value]) => (
            <div key={label} className="summary-item">
              <span>{label}</span>
              <strong>{value}</strong>
            </div>
          ))}
        </div>
        <button className="start-create" onClick={onStart}>Start Create</button>
      </div>
    </div>
  );
}

function SignupIntentModal({ close }: { close: () => void }) {
  return (
    <div className="modal-backdrop" role="dialog" aria-modal="true">
      <div className="signup-modal">
        <button className="modal-close" onClick={close}>×</button>
        <p className="mini-kicker">Your AI lover is ready</p>
        <h2>Join free to unlock her private chat preview.</h2>
        <p>First subscription offer: 70% off. No public profile, no shared content.</p>
        <input placeholder="Email address" type="email" />
        <button>Continue</button>
        <small>This is a conversion-intent placeholder for the first landing-page test.</small>
      </div>
    </div>
  );
}

export default function CreateQuiz() {
  const [currentStep, setCurrentStep] = useState(0);
  const [state, setState] = useState<QuizState>(initialQuizState);
  const [showModal, setShowModal] = useState(false);

  const progressLabel = useMemo(() => `${currentStep + 1} / ${steps.length}`, [currentStep]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const attribution = readAttribution(params);
    persistAttribution(attribution);
    trackLandingEvent('landing_view', { attribution, page: 'land_create' });
  }, []);

  function update(patch: Partial<QuizState>) {
    setState((previous) => ({ ...previous, ...patch }));
  }

  function next() {
    trackLandingEvent('quiz_step_complete', { step: steps[currentStep].label, state });
    setCurrentStep((step) => Math.min(step + 1, steps.length - 1));
  }

  function back() {
    setCurrentStep((step) => Math.max(step - 1, 0));
  }

  function randomizeName() {
    const first = firstNames[Math.floor(Math.random() * firstNames.length)];
    const last = lastNames[Math.floor(Math.random() * lastNames.length)];
    update({ name: `${first} ${last}` });
  }

  function startCreate() {
    trackLandingEvent('start_create_click', { state });
    setShowModal(true);
  }

  return (
    <main className="create-shell">
      <div className="ambient ambient-one" />
      <div className="ambient ambient-two" />
      <header className="create-header">
        <Link className="vx-brand" href="/">Vxlover</Link>
        <span>{progressLabel}</span>
      </header>

      <Stepper currentStep={currentStep} setStep={setCurrentStep} />

      <section className="quiz-stage">
        {currentStep === 0 && <StyleStep state={state} update={update} />}
        {currentStep === 1 && <LookStep state={state} update={update} />}
        {currentStep === 2 && <FaceStep state={state} update={update} />}
        {currentStep === 3 && <BodyStep state={state} update={update} />}
        {currentStep === 4 && <DetailsStep state={state} update={update} randomizeName={randomizeName} />}
        {currentStep === 5 && <ReviewStep state={state} onStart={startCreate} />}
      </section>

      {currentStep < 5 ? (
        <div className="bottom-controls">
          <button className="round-control" onClick={back} disabled={currentStep === 0}>‹</button>
          <button className="continue-button" onClick={next}>Continue</button>
        </div>
      ) : (
        <div className="bottom-controls review-controls">
          <button className="round-control" onClick={back}>‹</button>
        </div>
      )}

      {showModal && <SignupIntentModal close={() => setShowModal(false)} />}
    </main>
  );
}
